import React, {useCallback, useMemo} from "react";
import {Caption, Link, Title} from "./Texts";
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	Grid,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography
} from "@mui/material";
import {useAppDispatch, useAppSelector} from "../../store";
import {setPreferencesPartial} from "../../store/expertise/expertise.reducer";
import {TypeFormulaireSollicitation, TypeSollicitation} from "../../../core/apis/monsisra/generated";
import {TypeSollicitationCtx} from "../../../index";

export function FormulaireSollicitation() {
	const dispatch = useAppDispatch();
	const isAdminMode = useAppSelector((s) => s.auth.isAdminMode);

	const {preferences, formulaires} = useAppSelector((s) => s.expertise);

	const onChange = React.useCallback(
		(e: SelectChangeEvent) => {
			dispatch(
				setPreferencesPartial({
					idTypeFormulaire: e.target.value,
				})
			);
		},
		[dispatch]
	);

	const typeSollicitation = React.useContext(TypeSollicitationCtx);

	const text = typeSollicitation === TypeSollicitation.TeleExpertise ? "téléexpertise" : "demande d'appui";

	const { allFormulairesDisplayed, isSelectedFormRestrictedToAdmin, minWidth } = useMemo(() => {
		const baseFormulaires = [{ nomFormulaire: "Aucun", idTypeFormulaire: "null" }, ...formulaires];

		// Formulaire actuellement sélectionné (dans les préférences)
		const selectedForm = formulaires.find((f: TypeFormulaireSollicitation) => f.idTypeFormulaire === preferences?.idTypeFormulaire)!;

		// Formulaires visibles pour l'utilisateur
		const visibleForms = baseFormulaires.filter(form =>
			form.idTypeFormulaire === "null" || !form.adminOnly || isAdminMode
		);

		// Si le formulaire sélectionné a été filtré (adminOnly sans accès), on le réinjecte pour l'afficher mais il sera grisé
		const isSelectedFiltered = selectedForm?.adminOnly && !isAdminMode &&
			!visibleForms.some(f => f.idTypeFormulaire === selectedForm.idTypeFormulaire);

		if (isSelectedFiltered) {
			visibleForms.push(selectedForm);
		}

		// Vérification si le formulaire actuel est adminOnly et non accessible
		const isSelectedFormRestrictedToAdmin = selectedForm?.adminOnly && !isAdminMode;

		// Calcul de la taille minimale de l'input afin d'éviter que le texte se déplace en fonction de la valeur choisie
		const minWidth = formulaires.reduce((acc, current) => {
			if (acc < current.nomFormulaire.length) return current.nomFormulaire.length;
			return acc;
		}, 0) + 2 + "ch";

		return {
			allFormulairesDisplayed: visibleForms,
			isSelectedFormRestrictedToAdmin,
			minWidth
		};
	}, [formulaires, isAdminMode, preferences?.idTypeFormulaire]);

	const onCheckboxSelected = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			dispatch(
				setPreferencesPartial({
					modeChatActif: e.target.checked,
				})
			);
		},
		[dispatch]
	);


	if (!preferences) return null;

	return (
		<Grid component={"section"} container direction={"column"} spacing={2}>
			<Grid item>
				<Title text={`Formulaire de ${text}`}/>
			</Grid>
			<Grid item container spacing={1} direction={"column"}>
				<Grid item mb={1}>
					<Caption
						text={
							<Grid container spacing={2}>
								<Grid item>
									Vous pouvez proposer un formulaire structuré de {text} que le demandeur devra remplir. Pour en
									savoir plus sur les différents modèles, contactez votre référent ou l'<Link text={"assistance"} href={"https://www.sante-ara.fr/assistance-technique/"} /> Sara.
								</Grid>
							</Grid>
						}
					/>
				</Grid>
				<Grid item container alignItems={"center"} spacing={2}>
					<Grid item>
						<Typography>Choix du modèle de formulaire</Typography>
					</Grid>

					<Grid item minWidth={minWidth}>
						<FormControl fullWidth>
							<Select
								variant={"standard"}
								className={"input"}
								id="demo-simple-select"
								value={preferences?.idTypeFormulaire ?? "null"}
								onChange={onChange}
								disabled={isSelectedFormRestrictedToAdmin}
							>
								{allFormulairesDisplayed.map((form) => (
									<MenuItem
										value={form.idTypeFormulaire}
										key={form.idTypeFormulaire}
										disabled={
											form.idTypeFormulaire === preferences?.idTypeFormulaire &&
											form.adminOnly &&
											!isAdminMode
										}
									>
										{form.nomFormulaire} {isAdminMode && form.idTypeFormulaire !== "null" && `(${form.idTypeFormulaire})`}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
				{isAdminMode &&
                    <Grid item>
                        <FormControlLabel
                            control={<Checkbox onChange={onCheckboxSelected}
							                   checked={preferences?.modeChatActif}/>}
                            label={"Je souhaite que la fonctionnalité conversation soit activée après l'enregistrement du formulaire"}
                        />
                    </Grid>
				}
			</Grid>
		</Grid>
	);
}
